'use client';

import { useState, useCallback } from 'react';
import { useTealium } from '@fshr/context/tealium-context';
import Image from '../image';
import { IconChevronLightLeft, IconChevronLightRight } from '../icon';
import type { EditorialOverviewImage } from './editorial-overview.types';

type EditorialOverviewGalleryProps = {
	images: EditorialOverviewImage[];
};

export default function EditorialOverviewGallery({ images }: EditorialOverviewGalleryProps) {
	const [activeIndex, setActiveIndex] = useState(0);
	const { trackInteraction } = useTealium();

	const totalImages = images.length;

	const goToNext = useCallback(() => {
		setActiveIndex(prev => (prev + 1) % totalImages);
		trackInteraction('product detail page', 'signature image carousel', 'image carousel icon');
	}, [totalImages, trackInteraction]);

	const goToPrev = useCallback(() => {
		setActiveIndex(prev => (prev - 1 + totalImages) % totalImages);
		trackInteraction('product detail page', 'signature image carousel', 'image carousel icon');
	}, [totalImages, trackInteraction]);

	if (totalImages === 0) return;

	// Single image case — no controls
	if (totalImages === 1) {
		return (
			<div className="w-full">
				<div className="relative w-full aspect-[16/9] overflow-hidden">
					<Image
						roughSize="m"
						src={images[0].url}
						alt={images[0].altText ?? 'Gallery image'}
						className="w-full h-full object-cover"
					/>
				</div>
			</div>
		);
	}

	return (
		<div className="w-full">
			{/* Main image with arrow overlays */}
			<div className="relative w-full aspect-[16/9] overflow-hidden group">
				<Image
					roughSize="m"
					src={images[activeIndex].url}
					alt={images[activeIndex].altText ?? `Gallery image ${activeIndex + 1}`}
					className="w-full h-full object-cover"
				/>

				{/* Desktop-only navigation arrows */}
				<div className="hidden lg:flex absolute inset-0 items-center justify-between px-inline-2xs opacity-0 group-hover:opacity-100 transition-opacity duration-200">
					<button
						type="button"
						onClick={goToPrev}
						aria-label="Previous image"
						className="flex items-center justify-center w-300 h-300 rounded-full bg-button-on-image-primary transition-colors duration-200 hover:bg-button-on-image-primary-hover focus-visible:bg-button-on-image-primary-hover active:bg-button-on-image-primary-pressed"
					>
						<IconChevronLightLeft width={12} height={12} />
					</button>
					<button
						type="button"
						onClick={goToNext}
						aria-label="Next image"
						className="flex items-center justify-center w-300 h-300 rounded-full bg-button-on-image-primary transition-colors duration-200 hover:bg-button-on-image-primary-hover focus-visible:bg-button-on-image-primary-hover active:bg-button-on-image-primary-pressed"
					>
						<IconChevronLightRight width={12} height={12} />
					</button>
				</div>
			</div>

			{/* Thumbnail strip */}
			<div className="flex flex-wrap items-center gap-x-inline-s gap-y-stack-s mt-stack-s">
				{images.map((image, index) => (
					<button
						key={index}
						type="button"
						onClick={() => setActiveIndex(index)}
						aria-label={`View image ${index + 1}`}
						className={`w-[60px] h-[60px] lg:w-[77px] lg:h-[77px] flex-shrink-0 overflow-hidden rounded-none border transition-colors duration-200 ${
							index === activeIndex ? 'border-black' : 'border-neutral-300 hover:border-black'
						}`}
					>
						<Image
							roughSize="s"
							src={image.url}
							alt={image.altText ?? `Thumbnail ${index + 1}`}
							className="w-full h-full object-cover"
						/>
					</button>
				))}
			</div>
		</div>
	);
}
